import React, { useEffect, useMemo } from "react";
import { Table, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import { useSaleSoModel } from "../saleSoModel";
import { ISaleSoDetail } from "../saleSoTypes";
import { useDictModel } from "@/views/base/dict/dictModel";
import YhTag from "@/components/YhTag";
import { momentFormat, formatNum } from "@/utils/utils";
import useNavModel from "@/model/navModel";
import { tableSize } from "@/utils/constants";
import YhTootip from "@/components/YhTootip";
import TextPriviewImage from "../TextPriviewImage";
import { invoiceModel } from "../InvoiceModel";
import { roleColumnModel } from "@/utils/roleColumn";
import useGlobalModel from "@/model/globalModel";

type statusType = {
    "sale-status.write-off": string,
    "sale.change": string,
    "sale.stock-io": string,
    "sale.settle": string,
    "sale.create": string,
    [key: string]: any
}


export function DataTable() {
    const { imageUrl } = invoiceModel();
    const {
        dataSource, selectedRowKeys, pgCfg, descRows, rowsDescTotal, descTotal,
        loadData, setSelectedRowKeys, setDescRows, reset
    } = useSaleSoModel();
    const { getTreeNodeName } = useDictModel();
    const { user: { roles } } = useGlobalModel();
    const { selectedNavId, replaceNav, findNav } = useNavModel();
    const { getRoleColumns } = roleColumnModel();

    useEffect(() => {
        loadData();
    }, [])

    const onLook = (code: string) => {
        let supplierEditKey = "5-2-3";
        replaceNav(
            selectedNavId,
            supplierEditKey,
            { id: code, mode: "look", },
            true
        );
    }
    const statusColor: statusType = {
        "sale-status.write-off": "green",
        "sale.change": "red",
        "sale.stock-io": "cyan",
        "sale.settle": "green",
        "sale.create": "blue",
    }

    const defaultColumns: ColumnsType<ISaleSoDetail> = [
        {
            title: '序号',
            fixed: "left",
            width: 60,
            render: (t, r, i) => i + 1
        }, {
            title: '销售单号',
            width: 120,
            dataIndex: 'saleCode',
            render: (text: any) => <YhTootip text={text} onClick={() => onLook(text)} />,
            // render: (text: any) => <Tooltip placement="topLeft" title={text}><p className="ellipsis click-span" onClick={(e) => { e.stopPropagation(); onLook(text) }}>{text}</p></Tooltip>,
        }, {
            title: '调拨单号',
            width: 190,
            dataIndex: 'transferOrder',
            render: (text: any) => <YhTootip text={text} />,

        }, {
            title: '单据状态',
            width: 80,
            dataIndex: 'status',
            render: (text) => <YhTag color={statusColor[text]} text={getTreeNodeName(text)} />,

        }, {
            title: '结算类型',
            width: 80,
            dataIndex: 'payType',
            render: (text) => getTreeNodeName(text)
        }, {
            title: '支付状态',
            width: 80,
            dataIndex: 'payStatus',
            render: (text) => getTreeNodeName(text)
        },
        {
            title: '退货状态',
            width: 80,
            dataIndex: 'returnStatus',
            render: (text) => getTreeNodeName(text)
        }, {
            title: '客户名称',
            width: 150,
            dataIndex: 'customerName',
            render: (text: any) => <YhTootip text={text} />,
        }, {
            title: '联系人',
            width: 150,
            dataIndex: 'contact',
        }, {
            title: '联系电话',
            width: 95,
            dataIndex: 'phone',
        }, {
            title: '车辆类型',
            width: 150,
            dataIndex: 'vehicleTypeName',
            render: (text: any) => <YhTootip text={text} style={{ width: '150px' }} />,
        }, {
            title: '销售类型',
            width: 100,
            dataIndex: 'salesTypeName',
        }, {
            title: '销售数量',
            width: 100,
            dataIndex: 'totalNum',
        }, {
            title: '销售金额',
            width: 100,
            dataIndex: 'totalAmount',
            render: (text) => `￥${text}`
        }, {
            title: '优惠金额',
            width: 100,
            dataIndex: 'discountTotalAmount',
            render: (text) => `￥${text}`
        }, {
            // title: '应收金额',
            title: '营业额',
            width: 100,
            dataIndex: 'receivedAmount',
            render: (text) => `￥${text}`
        }, {
            title: '已收金额',
            width: 100,
            dataIndex: 'receivableAmount',
            render: (text) => `￥${text ?? 0}`
        }, {
            title: '销售成本',
            width: 100,
            dataIndex: 'totalCost',
            render: (text) => `￥${text}`
        }, {
            title: '销售毛利',
            width: 100,
            dataIndex: 'grossProfit',
            render: (text) => `￥${text}`
        }, {
            title: '退货数量',
            width: 80,
            dataIndex: 'returnTotalNum',
        }, {
            title: '开票状态',
            width: 80,
            dataIndex: 'invoiceStatus',
            render: (text) => text == "1" ? "已开票" : "未开票"
        }, {
            title: '发票类型',
            width: 160,
            dataIndex: 'invoiceTypeName',
        }, {
            title: '发票编号',
            width: 80,
            dataIndex: 'invoiceNum',
            ellipsis: true,
            render: (text, record) => {
                return (
                    <TextPriviewImage text={text} previewList={record.invoiceMd5?.split(",")} imageUrl={imageUrl} />
                )
            },
        }, {
            title: '结算方',
            width: 90,
            dataIndex: 'settlementPartyName',
            render: (text: any) => <YhTootip text={text} />,
        }, {
            title: '结算方式',
            width: 120,
            dataIndex: 'paymentMethodName',
            // render: (text: any) => <Tooltip placement="topLeft" title={text}><p className="ellipsis">{getTreeNodeName(text) ? getTreeNodeName(text) : text}</p></Tooltip>,
            render: (text: any, record: any) => <Tooltip placement="topLeft" title={record?.settleDetails?.[0]?.payMethodName ?? record?.settleDetails?.[0]?.accountName}><p className="ellipsis">{record?.settleDetails?.[0]?.payMethodName ?? record?.settleDetails?.[0]?.accountName}</p></Tooltip>,

        }, {
            title: '销售员',
            width: 70,
            dataIndex: 'saleUser',
        }, {
            title: '制单员',
            width: 70,
            dataIndex: 'createUser',
            render: (text: any) => <YhTootip text={text} />,
        }, {
            title: '制单时间',
            width: 130,
            dataIndex: 'createTime',
            render: (text) => momentFormat(text)
        }, {
            title: '库管员',
            width: 70,
            dataIndex: 'storekeeper',
            render: (text: any) => <YhTootip text={text} />,
        }, {
            title: '出库时间',
            width: 130,
            dataIndex: 'stockIoTime',
            render: (text) => momentFormat(text)
        }, {
            title: '结算员',
            width: 130,
            dataIndex: 'reckoner',
        }, {
            title: '结算时间',
            width: 130,
            dataIndex: 'settleTime',
            render: (text) => momentFormat(text)
        }, {
            title: '核销时间',
            width: 130,
            dataIndex: 'writeOffTime',
            render: (text) => momentFormat(text)
        }, {
            title: '转接单号',
            width: 120,
            dataIndex: 'saleOrderCode',
            render: (text: any) => <YhTootip text={text} />,
        }, {
            title: '车牌号',
            width: 80,
            dataIndex: 'licensePlate',
            render: (text: any) => <YhTootip text={text} />,
        }, {
            title: 'VIN码',
            width: 80,
            dataIndex: 'vin',
            render: (text: any) => <YhTootip text={text} />,
        }, {
            title: '作废原因',
            width: 120,
            dataIndex: 'cancelReason',
        }, {
            title: '财务备注',
            width: 120,
            dataIndex: 'fmsNote',
            render: (text: any) => <YhTootip text={text} />,
        }, {
            title: '备注',
            width: 120,
            dataIndex: 'note',
            render: (text: any) => <YhTootip text={text} />,
        }, {
            title: '调出单号',
            width: 180,
            dataIndex: 'allocationCode',
        }, {
            title: '打印次数',
            width: 80,
            // fixed: "right",
            align: "center",
            dataIndex: 'printingTimes',
        },
        {
            title: '所属站点',
            width: 250,
            fixed: "right",
            dataIndex: 'shopName',
            render: (text: any) => <YhTootip text={text} style={{ width: 250 }} />,
        },
    ];

    const roleColumns = useMemo(() => {
        //根据可视化配置处理需要展示相关列
        const params = { "code": "sale", "roleCodes": roles };
        return getRoleColumns("sale", "主单", defaultColumns, params);
    }, [roles, dataSource]);
    //点击行选高亮
    const onRow = (record: ISaleSoDetail) => {
        let { id } = record as { id: string }
        let findIndexDesc = descRows.findIndex((item) => (item.id === id))
        let newDescRows = [...descRows];
        let newSelectedRowKeys = [...selectedRowKeys];

        if (findIndexDesc === -1) {
            //统计数据
            newDescRows.push(record)
            setDescRows(newDescRows)
            //表格选中行Key值
            newSelectedRowKeys.push(id);
            setSelectedRowKeys(newSelectedRowKeys);
        } else {
            //统计数据
            newDescRows.splice(findIndexDesc, 1);
            setDescRows(newDescRows);
            //表格选中行Key值
            newSelectedRowKeys.splice(findIndexDesc, 1);
            setSelectedRowKeys(newSelectedRowKeys);
        }
    };
    const onRightClick = (record: ISaleSoDetail) => {
        setDescRows([record])
        setSelectedRowKeys([record.id as string])
    }
    return (
        <Table
            bordered
            style={{ cursor: "pointer" }}
            size={tableSize}
            rowKey={record => record.id as string}
            rowSelection={{
                onChange: (selectedRowKeys: React.Key[], selectedRows: ISaleSoDetail[]) => {
                    setSelectedRowKeys(selectedRowKeys as string[]);
                    setDescRows(selectedRows)
                },
                selectedRowKeys
            }}
            columns={roleColumns}
            dataSource={dataSource}
            pagination={{
                showSizeChanger: true,
                ...pgCfg,
                defaultPageSize: 25,
                pageSizeOptions: ['15', '25', '50', '100', '200', '500'],
                position: ["bottomRight"],
                showTotal: () => `共${pgCfg.total}条`,
                onChange: (pageNum, pageSize) => {
                    loadData({ pageNum, pageSize })
                }
            }}
            onRow={record => ({
                // onClick: () => onRow(record.id as string),
                onClick: () => onRow(record),
                onContextMenu: (e) => {
                    e.preventDefault();
                    onRightClick(record)
                }
            })}
            scroll={{ x: 'max-content', y: 'calc(100vh - 370px)' }}
            summary={() => (
                <Table.Summary fixed>
                    <Table.Summary.Row className="table-total-box">
                        <Table.Summary.Cell index={0}>
                            <div className="title">合计</div>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} />
                        <Table.Summary.Cell index={2} />
                        <Table.Summary.Cell index={3} />
                        <Table.Summary.Cell index={4} />
                        <Table.Summary.Cell index={5} />
                        <Table.Summary.Cell index={6} />
                        <Table.Summary.Cell index={7} />
                        <Table.Summary.Cell index={8} />
                        <Table.Summary.Cell index={9} />
                        <Table.Summary.Cell index={10} />
                        <Table.Summary.Cell index={11} />
                        <Table.Summary.Cell index={12} />
                        <Table.Summary.Cell index={13}>
                            <span className="red">{selectedRowKeys.length === 0 ? descTotal.totalNum : rowsDescTotal.totalNum}</span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={14} />
                        {/* >
                            <span className="red">{formatNum(selectedRowKeys.length === 0 ? descTotal.totalAmount : rowsDescTotal.totalAmount)}</span>
                        </Table.Summary.Cell> */}
                        <Table.Summary.Cell index={15} />
                        {/* >
                            <span className="red">{formatNum(selectedRowKeys.length === 0 ? descTotal.discountTotalAmount : rowsDescTotal.discountTotalAmount)}</span>
                        </Table.Summary.Cell> */}
                        <Table.Summary.Cell index={16} />
                        {/* >
                            <span className="red">{formatNum(selectedRowKeys.length === 0 ? descTotal.receivedAmount : rowsDescTotal.receivedAmount)}</span>
                        </Table.Summary.Cell> */}
                        <Table.Summary.Cell index={17} />
                        {/* >
                            <span className="red">{formatNum(selectedRowKeys.length === 0 ? descTotal.receivableAmount : rowsDescTotal.receivableAmount)}</span>
                        </Table.Summary.Cell> */}
                        <Table.Summary.Cell index={18} />
                        {/* >
                            <span className="red">{formatNum(selectedRowKeys.length === 0 ? descTotal.latestPurPrice : rowsDescTotal.latestPurPrice)}</span>
                        </Table.Summary.Cell> */}
                        <Table.Summary.Cell index={19} />
                        {/* >
                            <span className="red">{formatNum(selectedRowKeys.length === 0 ? descTotal.grossProfit : rowsDescTotal.grossProfit)}</span>
                        </Table.Summary.Cell> */}
                        <Table.Summary.Cell index={20} />
                        <Table.Summary.Cell index={21} />
                        <Table.Summary.Cell index={22} />
                        <Table.Summary.Cell index={23} />
                        <Table.Summary.Cell index={24} />
                        <Table.Summary.Cell index={25} />
                        <Table.Summary.Cell index={26} />
                        <Table.Summary.Cell index={27} />
                        <Table.Summary.Cell index={28} />
                        <Table.Summary.Cell index={29} />
                        <Table.Summary.Cell index={30} />
                        <Table.Summary.Cell index={31} />
                        <Table.Summary.Cell index={32} />
                        <Table.Summary.Cell index={33} />
                        <Table.Summary.Cell index={34} />
                        <Table.Summary.Cell index={35} />
                        <Table.Summary.Cell index={36} />
                        <Table.Summary.Cell index={37} />
                        <Table.Summary.Cell index={38} />
                        <Table.Summary.Cell index={39} />
                        <Table.Summary.Cell index={40} />
                        <Table.Summary.Cell index={41} />
                        <Table.Summary.Cell index={42} />
                    </Table.Summary.Row>
                </Table.Summary>
            )}
        />
    )
}
